<div class="user-card-link-container">
  <div class="card-header">
    <h4>
      <span *ngIf="currentStep === 'search'">Recent Visitors</span>
      <span *ngIf="currentStep === 'verify'">Verify User</span>
      <span *ngIf="currentStep === 'consent'">User Consent</span>
      <span *ngIf="currentStep === 'success'">Success</span>
    </h4>
    <button nbButton ghost (click)="close()">
      <nb-icon icon="close-outline"></nb-icon>
    </button>
  </div>
  
  <!-- Steps indicator -->
  <div class="steps-indicator">
    <div class="step" [class.active]="currentStep === 'search'">1. Search</div>
    <div class="step" [class.active]="currentStep === 'verify'">2. Verify</div>
    <div class="step" [class.active]="currentStep === 'consent'">3. Consent</div>
    <div class="step" [class.active]="currentStep === 'success'">4. Complete</div>
  </div>
  
  <!-- Card UID Display -->
  <div class="card-uid-display">
    Card UID: <code>{{ cardUID }}</code>
  </div>
  
  <!-- Loading indicator -->
  <div *ngIf="loading" class="loading-container">
    <nb-spinner status="primary"></nb-spinner>
  </div>
  
  <!-- Error display -->
  <div *ngIf="error && !loading" class="error-container">
    <nb-alert status="danger">{{ error }}</nb-alert>
  </div>
  
  <!-- Step 1: Search Recent Visitors -->
  <div class="search-users" *ngIf="currentStep === 'search'">
    <!-- Header -->
    <div class="step-header">
      <h4>Search for user to link card {{ cardUID }}</h4>
      <small>Find recent visitors to link the card</small>
    </div>
    
    <!-- Loading and error states -->
    <div *ngIf="loading" class="loading-container">
      <ngx-spinner type="ball-spin-clockwise" bdColor="rgba(0,0,0,0.05)" color="#5DCFE3"></ngx-spinner>
      <div class="loading-text">Loading users...</div>
    </div>
    
    <div *ngIf="error && !loading" class="error-message">
      <p>{{ error }}</p>
      <button nbButton status="primary" (click)="loadRecentVisitors()">Try Again</button>
    </div>
    
    <!-- Search form -->
    <form [formGroup]="searchForm" (ngSubmit)="searchUsers()" *ngIf="!loading && !error">
      <div class="search-container">
        <nb-form-field>
          <input
            type="text"
            nbInput
            fullWidth
            placeholder="Search by name, email or phone"
            formControlName="searchTerm"
          />
        </nb-form-field>
        <button 
          nbButton 
          status="primary" 
          [disabled]="searchForm.invalid"
          type="submit"
        >
          Search
        </button>
        
        <button 
          nbButton 
          status="basic" 
          (click)="loadRecentVisitors()"
          type="button"
          nbTooltip="Refresh recent visitors list"
        >
          <nb-icon icon="refresh-outline"></nb-icon>
        </button>
      </div>
    </form>
    
    <!-- Search results -->
    <div class="search-results" *ngIf="!loading && !error">
      <div class="results-header">
        <h5>Recent Store Visitors (Last {{ timeWindowMinutes }} minutes)</h5>
        <small *ngIf="filteredUsers.length">Showing {{ filteredUsers.length }} visitor(s)</small>
        <small *ngIf="!filteredUsers.length">No matching visitors found</small>
      </div>
      
      <div class="user-list" *ngIf="filteredUsers.length > 0">
        <div class="user-card" *ngFor="let user of filteredUsers" (click)="selectUser(user)">
          <div class="user-info">
            <h6 class="user-name">{{ user.fullName || 'No Name' }}</h6>
            <div class="user-details">
              <span *ngIf="user.email"><nb-icon icon="email-outline"></nb-icon> {{ user.email }}</span>
              <span *ngIf="user.phone"><nb-icon icon="phone-outline"></nb-icon> {{ user.phone }}</span>
            </div>
          </div>
          <div class="user-actions">
            <button nbButton status="primary" size="small">Select</button>
          </div>
        </div>
      </div>
      
      <div class="no-results" *ngIf="filteredUsers.length === 0">
        <nb-icon icon="person-outline" class="no-results-icon"></nb-icon>
        <p>No users found matching your search criteria</p>
        <button nbButton status="primary" (click)="loadRecentVisitors()">Refresh List</button>
      </div>
    </div>
    
    <!-- Action buttons -->
    <div class="dialog-actions">
      <button nbButton status="basic" (click)="close()">Cancel</button>
    </div>
  </div>
  
  <!-- Step 2: Verify User -->
  <div *ngIf="currentStep === 'verify' && !loading">
    <h5>Verify User Information</h5>
    
    <div class="user-verification-info">
      <div class="info-row"><strong>Name:</strong> {{ selectedUser.fullName || 'Not provided' }}</div>
      <div class="info-row"><strong>Email:</strong> {{ selectedUser.email || 'Not provided' }}</div>
      <div class="info-row"><strong>Phone:</strong> {{ selectedUser.phone || 'Not provided' }}</div>
      <div class="info-row" *ngIf="selectedUser.visitTime"><strong>Last Visit:</strong> {{ selectedUser.visitTime | date:'medium' }}</div>
    </div>
    
    <div class="verification-actions">
      <button nbButton status="basic" (click)="goBack()">Back</button>
      <button nbButton status="primary" (click)="goToConsentStep()">Confirm & Continue</button>
    </div>
  </div>
  
  <!-- Step 3: Consent -->
  <div *ngIf="currentStep === 'consent' && !loading">
    <div class="consent-text">
      <h5>Privacy Policy & Consent</h5>
      <p>By linking this card to your account, you consent to:</p>
      <ul>
        <li>Your card access being tracked for security purposes</li>
        <li>Your personal information being stored securely</li>
        <li>Receiving notifications related to card access</li>
      </ul>
      <p class="signature-instruction">Please sign below to confirm your consent:</p>
    </div>
    
    <div class="signature-pad-container">
      <canvas id="signaturePadCanvas" width="450" height="180" style="border: 1px solid #ddd; background-color: #f5f5f5; border-radius: 4px;"></canvas>
      
      <div class="signature-status">
        <span *ngIf="signatureIsEmpty" class="text-danger">
          <nb-icon icon="alert-circle-outline"></nb-icon> Signature required
        </span>
        <span *ngIf="!signatureIsEmpty" class="text-success">
          <nb-icon icon="checkmark-circle-outline"></nb-icon> Signature captured
        </span>
      </div>
      
      <div class="signature-actions">
        <button nbButton size="small" status="basic" (click)="clearSignature()">
          <nb-icon icon="trash-outline"></nb-icon> Clear
        </button>
        <button nbButton size="small" status="success" (click)="confirmSignature()" [disabled]="signatureIsEmpty">
          <nb-icon icon="checkmark-outline"></nb-icon> Confirm Signature
        </button>
      </div>
    </div>
    
    <div class="consent-actions">
      <button nbButton status="basic" (click)="goBack()">Back</button>
      <button 
        nbButton 
        status="primary" 
        [disabled]="!signatureConfirmed" 
        (click)="submitLinkRequest()">
        Submit
      </button>
    </div>
  </div>
  
  <!-- Step 4: Success -->
  <div *ngIf="currentStep === 'success' && !loading">
    <div class="success-container">
      <div class="success-icon">
        <nb-icon icon="checkmark-circle-2-outline"></nb-icon>
      </div>
      <h5>Card Successfully Linked</h5>
      <p>The card has been successfully linked to {{ selectedUser.fullName }}'s account.</p>
      <button nbButton status="primary" (click)="close(true)">Close</button>
    </div>
  </div>
</div>

<ngx-spinner bdColor="rgba(51,51,51,0.8)" size="medium" [fullScreen]="false" color="white" type="ball-scale-multiple">
  <p style="color: white">Processing...</p>
</ngx-spinner> 